import React, { useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import { initialValues, getValidationSchema } from '../../config';
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Grid,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import jwt_decode from 'jwt-decode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getLeaseByIdForTenantSignature } from '../../utils';
import { Step1, Step2, Step3, Step4, Step5, Step6, Step7 } from './lease';
import { Step1 as DesktopStep1, Step2 as DesktopStep2, Step3 as DesktopStep3, Step4 as DesktopStep4, Step5 as DesktopStep5, Step6 as DesktopStep6, Step7 as DesktopStep7 } from '../DesktopView/lease';
import { FormValidation, ThemeWrapper, publishRequestedTenantUpdates, uploadLeasePdf } from '../../utils';
import './lease.scss';
import Swal from 'sweetalert2';
import { Footer } from './Footer';

// ─── Submission Progress Swal Helper ─────────────────────────────────────────
const showSubmissionLoader = ({ title = 'Processing...', message = '', percent = 0 }) => {
  Swal.fire({
    title: `<span style="font-family:'Segoe UI',sans-serif;font-size:1.1rem;font-weight:700;color:#183B4E">${title}</span>`,
    html: `
      <div style="font-family:'Segoe UI',sans-serif;padding:8px 0 4px">
        <p style="color:#555;font-size:0.85rem;margin:0 0 16px;min-height:20px">${message}</p>
        <div style="
          background:#E8EDF0;
          border-radius:999px;
          height:12px;
          overflow:hidden;
          box-shadow:inset 0 1px 3px rgba(0,0,0,0.12);
        ">
          <div id="swal-progress-bar" style="
            height:100%;
            width:${percent}%;
            border-radius:999px;
            background: linear-gradient(90deg,#1a5276,#2980b9,#27ae60);
            background-size:200% 100%;
            animation: shimmer 1.5s linear infinite;
            transition:width 0.4s ease;
          "></div>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px">
          <span style="font-size:0.72rem;color:#888">Please do not close this tab</span>
          <span id="swal-percent-label" style="
            font-size:1rem;
            font-weight:700;
            color:#183B4E;
            min-width:44px;
            text-align:right;
          ">${percent}%</span>
        </div>
        <style>
          @keyframes shimmer {
            0%   { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        </style>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    showCancelButton: false,
    backdrop: `rgba(24,59,78,0.55)`,
    customClass: {
      popup: 'swal-submission-popup',
    },
    didOpen: () => {
      if (!document.getElementById('swal-submission-style')) {
        const style = document.createElement('style');
        style.id = 'swal-submission-style';
        style.textContent = `
          .swal-submission-popup {
            border-radius: 16px !important;
            padding: 24px 24px 20px !important;
            box-shadow: 0 20px 60px rgba(0,0,0,0.25) !important;
            max-width: 360px !important;
          }
        `;
        document.head.appendChild(style);
      }
    },
  });
};

const updateSubmissionLoader = ({ title, message, percent }) => {
  const titleEl = Swal.getTitle();
  if (titleEl && title !== undefined) {
    titleEl.innerHTML = `<span style="font-family:'Segoe UI',sans-serif;font-size:1.1rem;font-weight:700;color:#183B4E">${title}</span>`;
  }
  const msgEl = document.querySelector('.swal2-html-container p');
  if (msgEl && message !== undefined) msgEl.textContent = message;
  const bar = document.getElementById('swal-progress-bar');
  if (bar && percent !== undefined) bar.style.width = `${percent}%`;
  const label = document.getElementById('swal-percent-label');
  if (label && percent !== undefined) label.textContent = `${percent}%`;
};
// ─────────────────────────────────────────────────────────────────────────────

const MobileView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formikData, setFormikData] = useState(null);
  const [token, setToken] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [leaseId, setLeaseId] = useState(null);
  const [isFullySigned, setIsFullySigned] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formikFormRef] = useState(React.createRef());
  const leaseFormRef = useRef(null);
  const desktopPdfRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Preparing document...');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [allDocumentsCompleted, setAllDocumentsCompleted] = useState(false);
  const [rentPaymentConsent, setRentPaymentConsent] = useState(false);
  const [utilitiesConsent, setUtilitiesConsent] = useState(false);
  const [tenantInsuranceConsent, setTenantInsuranceConsent] = useState(false);
  const [electronicSignatureConsent, setElectronicSignatureConsent] = useState(false);
  const [showDesktopForPDF, setShowDesktopForPDF] = useState(false);

  // ── Tab-close prevention ──────────────────────────────────────────────────
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isSubmittingRef.current) {
        e.preventDefault();
        e.returnValue =
          'Your lease submission is still in progress. Closing this tab may cause data loss. Are you sure?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const pathParts = window.location.pathname.split('/');
        const urlToken = pathParts[pathParts.length - 1];
        setToken(urlToken);

        if (!urlToken) {
          throw new Error('Invalid URL: No token found');
        }

        const decodedToken = jwt_decode(urlToken);
        setTenantId(decodedToken.tenantId);
        setLeaseId(decodedToken.leaseId);

        const response = await getLeaseByIdForTenantSignature(decodedToken.leaseId, urlToken);
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch lease data');
        }

        if (
          response.message ===
          'You have successfully signed all required documents and the lease.'
        ) {
          setIsFullySigned(true);
          setSuccessMessage(response.message);
        } else {
          const data = initialValues(response);
          setFormikData(data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const generatePDFFile = async (showProgressUpdates = false, swalProgress = null) => {
    try {
      if (!desktopPdfRef.current) {
        throw new Error('Desktop PDF reference is not available');
      }

      if (showProgressUpdates && !swalProgress) {
        setShowProgress(true);
        setProgressMessage('Generating PDF...');
        setProgress(5);
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const stepDivs = desktopPdfRef.current.querySelectorAll('.formik-step');
      const totalSteps = stepDivs.length - 1;

      if (totalSteps === 0) {
        throw new Error('No formik-step elements found for PDF generation');
      }

      for (let i = 0; i < totalSteps; i++) {
        const pageNum = i + 1;
        const pageMsg = `Capturing page ${pageNum} of ${totalSteps}...`;
        const barPct = 5 + Math.round((i / totalSteps) * 45);

        if (swalProgress) {
          updateSubmissionLoader({
            message: pageMsg,
            percent: swalProgress.base + Math.round((barPct / 100) * swalProgress.range),
          });
        } else if (showProgressUpdates) {
          setProgressMessage(pageMsg);
          setProgress(barPct);
        }

        const stepDiv = stepDivs[i];
        let canvas;
        try {
          canvas = await html2canvas(stepDiv, {
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
            windowWidth: 1400,
            width: 1200,
          });
        } catch (canvasError) {
          throw new Error(`Canvas generation failed for page ${pageNum}`);
        }

        const addPct = 50 + Math.round((i / totalSteps) * 30);
        if (swalProgress) {
          updateSubmissionLoader({
            percent: swalProgress.base + Math.round((addPct / 100) * swalProgress.range),
          });
        } else if (showProgressUpdates) {
          setProgress(addPct);
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        if (!imgData || imgData === 'data:,') {
          throw new Error(`Invalid image data for step ${pageNum}`);
        }

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

        const finalPct = 80 + Math.round((i / totalSteps) * 20);
        if (swalProgress) {
          updateSubmissionLoader({
            percent: swalProgress.base + Math.round((finalPct / 100) * swalProgress.range),
          });
        } else if (showProgressUpdates) {
          setProgress(finalPct);
        }
      }

      if (!swalProgress && showProgressUpdates) {
        setProgressMessage('Finalizing PDF...');
        setProgress(95);
      }

      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], 'Lease-Form.pdf', { type: 'application/pdf' });

      if (!swalProgress && showProgressUpdates) {
        setProgress(100);
        setProgressMessage('PDF generated successfully!');
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      return pdfFile;
    } catch (error) {
      if (!swalProgress && showProgressUpdates) {
        setProgressMessage(`Error generating PDF: ${error.message}`);
      }
      throw error;
    } finally {
      if (!swalProgress && showProgressUpdates) {
        setTimeout(() => {
          setShowProgress(false);
          setProgress(0);
        }, 1000);
      }
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      setShowDesktopForPDF(true);
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!desktopPdfRef.current) {
        throw new Error('Desktop view failed to render');
      }

      const pdfFile = await generatePDFFile(true);
      if (pdfFile) {
        const pdfBlob = new Blob([pdfFile], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Lease-Form.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      await Swal.fire({
        icon: 'error',
        title: 'PDF Generation Failed',
        text: 'An error occurred while generating the PDF. Please try again.',
        confirmButtonText: 'OK',
      });
    } finally {
      setShowDesktopForPDF(false);
      setTimeout(() => {
        setIsGeneratingPDF(false);
      }, 500);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: formikData || {},
    validationSchema: tenantId ? getValidationSchema(tenantId) : null,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!electronicSignatureConsent || !allDocumentsCompleted) {
          await Swal.fire({
            icon: 'warning',
            title: 'Incomplete Documents',
            text: 'Please complete all documents (upload, sign, or review) and consent to electronic signatures before submitting.',
            confirmButtonText: 'OK',
          });
          setSubmitting(false);
          return;
        }

        // ── Lock tab close & open loader ───────────────────────────────────
        isSubmittingRef.current = true;

        showSubmissionLoader({
          title: '📄 Preparing Your Submission',
          message: 'Getting things ready...',
          percent: 0,
        });
        // ──────────────────────────────────────────────────────────────────

        if (leaseId && token) {
          try {
            updateSubmissionLoader({
              title: '📄 Generating Lease PDF',
              message: 'Building your lease document...',
              percent: 5,
            });

            setShowDesktopForPDF(true);
            await new Promise(resolve => setTimeout(resolve, 100));

            if (!desktopPdfRef.current) {
              throw new Error('Desktop view failed to render');
            }

            const pdfFile = await generatePDFFile(false, { base: 5, range: 50 });

            if (pdfFile) {
              updateSubmissionLoader({
                title: '☁️ Uploading Lease PDF',
                message: 'Securely uploading your signed lease...',
                percent: 55,
              });
              await uploadLeasePdf(leaseId, pdfFile, token);
              updateSubmissionLoader({
                title: '☁️ Upload Complete',
                message: 'Lease PDF uploaded successfully.',
                percent: 75,
              });
            }
          } catch (uploadError) {
            console.error('Silent PDF upload failed:', uploadError);
            updateSubmissionLoader({
              title: '⚠️ Upload Notice',
              message: 'PDF upload encountered an issue. Continuing submission...',
              percent: 75,
            });
          } finally {
            setShowDesktopForPDF(false);
          }
        }

        updateSubmissionLoader({
          title: '✅ Finalising Submission',
          message: 'Saving your documents and signatures...',
          percent: 85,
        });

        const response = await publishRequestedTenantUpdates(values, token);

        updateSubmissionLoader({
          title: '🎉 All Done!',
          message: 'Everything has been saved successfully.',
          percent: 100,
        });

        await new Promise(resolve => setTimeout(resolve, 900));

        // ── Unlock tab close ───────────────────────────────────────────────
        isSubmittingRef.current = false;

        await Swal.fire({
          icon: 'success',
          title: 'Documents Processed Successfully!',
          confirmButtonText: 'OK',
        });

        if (response?.companyDomain) {
          window.location.href = response.companyDomain;
        } else {
          window.location.href = 'https://www.rentdigicare.com';
        }
      } catch (error) {
        console.error('Error in submission:', error);
        isSubmittingRef.current = false;
        setShowDesktopForPDF(false);
        Swal.close();
        await Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: 'An error occurred while processing your documents. Please try again.',
          confirmButtonText: 'OK',
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const tenant = formikData?.tenants?.find((t) => t?.id === tenantId);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="h6" component="div" sx={{ ml: 2 }}>
          Loading your documents...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Error</Typography>
          <Typography>{error}</Typography>
          <Typography>This link may have expired or is invalid.</Typography>
        </Alert>
      </Container>
    );
  }

  if (isFullySigned) {
    return (
      <ThemeWrapper>
        <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {successMessage}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Thank you for completing the lease signing process.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => (window.location.href = 'https://www.rentdigicare.com')}
            >
              Return to Home
            </Button>
          </Paper>
        </Container>
      </ThemeWrapper>
    );
  }

  const steps = [
    { label: 'Step 1', component: <Step1 formik={formik} token={token} /> },
    {
      label: 'Step 2', component: (
        <Step2
          formik={formik}
          token={token}
          rentPaymentConsent={rentPaymentConsent}
          setRentPaymentConsent={setRentPaymentConsent}
          utilitiesConsent={utilitiesConsent}
          setUtilitiesConsent={setUtilitiesConsent}
        />
      )
    },
    {
      label: 'Step 3', component: (
        <Step3
          formik={formik}
          token={token}
          tenantInsuranceConsent={tenantInsuranceConsent}
          setTenantInsuranceConsent={setTenantInsuranceConsent}
        />
      )
    },
    { label: 'Step 4', component: <Step4 formik={formik} token={token} /> },
    { label: 'Step 5', component: <Step5 formik={formik} token={token} /> },
    {
      label: 'Step 6',
      component: <Step6 formik={formik} token={token} electronicSignatureConsent={electronicSignatureConsent} setElectronicSignatureConsent={setElectronicSignatureConsent} />
    },
    {
      label: 'Step 7',
      component: <Step7 formik={formik} token={token} onCompletionStatusChange={setAllDocumentsCompleted} />
    },
  ];

  const desktop_steps = [
    { label: 'Step 1', component: <DesktopStep1 formik={formik} token={token} /> },
    {
      label: 'Step 2', component: (
        <DesktopStep2
          formik={formik}
          token={token}
          rentPaymentConsent={rentPaymentConsent}
          setRentPaymentConsent={setRentPaymentConsent}
          utilitiesConsent={utilitiesConsent}
          setUtilitiesConsent={setUtilitiesConsent}
        />
      )
    },
    {
      label: 'Step 3', component: (
        <DesktopStep3
          formik={formik}
          token={token}
          tenantInsuranceConsent={tenantInsuranceConsent}
          setTenantInsuranceConsent={setTenantInsuranceConsent}
        />
      )
    },
    { label: 'Step 4', component: <DesktopStep4 formik={formik} token={token} /> },
    { label: 'Step 5', component: <DesktopStep5 formik={formik} token={token} /> },
    {
      label: 'Step 6',
      component: <DesktopStep6 formik={formik} token={token} electronicSignatureConsent={electronicSignatureConsent} setElectronicSignatureConsent={setElectronicSignatureConsent} />
    },
    {
      label: 'Step 7',
      component: <DesktopStep7 formik={formik} token={token} onCompletionStatusChange={setAllDocumentsCompleted} />
    },
  ];

  return (
    <ThemeWrapper>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8, backgroundColor: '#F3F3E0' }}>
        <Container
          maxWidth="lg"
          sx={{
            backgroundColor: '#183B4E',
          }}
        >
          {showProgress && (
            <Snackbar
              open
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ width: '100%' }}
            >
              <Paper sx={{ p: 2, width: '100%', maxWidth: '500px' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {progressMessage}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
                >
                  {progress}%
                </Typography>
              </Paper>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ p: 2, backgroundColor: '#183B4E' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={6} md={6}>
                {tenant && (
                  <Typography variant="h6" color="primary.contrastText" style={{ fontSize: '1rem' }}>
                    Welcome, {tenant.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6} md={6}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  {formikData?.property && (
                    <Typography variant="body1" color="primary.contrastText">
                      Property: {formikData.property.property_name}
                      {formikData.property.unit_no && `, Unit ${formikData.property.unit_no}`}
                    </Typography>
                  )}
                  <Box display="flex" alignItems="center" mt={1}>
                    <Button
                      startIcon={<PrintIcon />}
                      sx={{ color: 'primary.contrastText', textTransform: 'none' }}
                      onClick={handleDownloadPDF}
                      disabled={isGeneratingPDF}
                    >
                      {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper className="shadow-lg lease-form-mobile" sx={{ p: 4, mb: 3 }}>
            <form onSubmit={formik.handleSubmit} ref={formikFormRef}>
              <div ref={leaseFormRef}>
                {steps.map((step, index) => (
                  <Box key={index} className={`${index !== steps.length - 1 ? 'mb-4' : ''} formik-step`}>
                    {step.component}
                    <Footer className="lease-form-footer-mobile" values={formik.values} page={index + 1} />
                    {index < steps.length - 1 && (
                      <Divider sx={{ my: 4, borderColor: 'gray' }} />
                    )}
                  </Box>
                ))}
              </div>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={formik.isSubmitting || !formik.isValid || isGeneratingPDF || !allDocumentsCompleted}
                  sx={{
                    transition: 'opacity 0.3s ease',
                    '&:disabled': {
                      opacity: 0.7,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  {formik.isSubmitting ? 'Processing...' : 'Submit All Documents'}
                </Button>
              </Box>

              <FormValidation formik={formik} rentPaymentConsent={!rentPaymentConsent} utilitiesConsent={!utilitiesConsent} tenantInsuranceConsent={!tenantInsuranceConsent} electronicSignatureValidation={!electronicSignatureConsent && !formik.isSubmitting} documentValidations={!allDocumentsCompleted && !formik.isSubmitting} />
            </form>
          </Paper>

          {showDesktopForPDF && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '1200px',
                height: '100vh',
                opacity: 0,
                pointerEvents: 'none',
                background: 'white',
                zIndex: -1,
                overflow: 'visible',
              }}
            >
              <Paper
                className="shadow-lg p-5 lease-form"
                sx={{
                  p: 4,
                  width: '1200px',
                  minHeight: 'auto',
                  backgroundColor: 'white',
                }}
              >
                <div ref={desktopPdfRef}>
                  {desktop_steps.map((step, index) => (
                    <Box
                      key={`pdf-${index}`}
                      className="formik-step"
                      sx={{
                        pageBreakAfter: index < desktop_steps.length - 1 ? 'always' : 'avoid',
                        minHeight: '1000px',
                        position: 'relative',
                      }}
                    >
                      {step.component}
                      <Footer values={formik.values} page={index + 1} />
                      {index < desktop_steps.length - 1 && (
                        <Divider sx={{ my: 4, borderColor: 'gray' }} />
                      )}
                    </Box>
                  ))}
                </div>
              </Paper>
            </div>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 4,
              backgroundColor: 'grey.100',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              If you have any questions, please contact your property manager (
              {formik.values.manager?.first_name} {formik.values.manager?.last_name},{' '}
              {formik.values.manager?.phone_no || 'N/A'}).
            </Typography>
          </Paper>
          <Box sx={{ height: 20, backgroundColor: '#183B4E' }} />
        </Container>
      </Container>
    </ThemeWrapper>
  );
};

export { MobileView };